import React, { useState, useEffect } from "react";

import { collection, getDocs, query } from "firebase/firestore";

import { db } from "../../Config/Firebase/config";

import "./carousel.css";

const Carousel = () => {
  const [sliderImages, setSliderImages] = useState([]);

  const [currentIndex, setCurrentIndex] = useState(0);

  const [loading, setLoading] = useState(true);

  // ============================================================
  // FETCH CAROUSEL DATA
  // ============================================================

  useEffect(() => {
    const CACHE_KEY = "carouselCache";

    const CACHE_EXPIRATION = 7 * 24 * 60 * 60 * 1000; // 7 days

    const carouselCollectionRef = collection(db, "carousel");

    const fetchCarouselImages = async () => {
      try {
        const q = query(carouselCollectionRef);

        const data = await getDocs(q);

        const images = data.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          // Only keep records that actually have an image URL
          .filter((item) => item.image && typeof item.image === "string");

        setSliderImages(images);

        setCurrentIndex(0);

        // Save to localStorage
        const cachedData = {
          data: images,
          timestamp: new Date().getTime(),
        };

        localStorage.setItem(CACHE_KEY, JSON.stringify(cachedData));
      } catch (error) {
        console.error("Error fetching carousel images:", error);
      } finally {
        setLoading(false);
      }
    };

    // ============================================================
    // CHECK CACHE
    // ============================================================

    try {
      const cachedData = localStorage.getItem(CACHE_KEY);

      if (cachedData) {
        const { data, timestamp } = JSON.parse(cachedData);

        const currentTime = new Date().getTime();

        if (
          Array.isArray(data) &&
          currentTime - timestamp <= CACHE_EXPIRATION
        ) {
          setSliderImages(data);

          setCurrentIndex(0);

          setLoading(false);
        } else {
          fetchCarouselImages();
        }
      } else {
        fetchCarouselImages();
      }
    } catch (error) {
      console.error("Error reading carousel cache:", error);

      fetchCarouselImages();
    }
  }, []);

  // ============================================================
  // AUTO SLIDE
  // ============================================================

  useEffect(() => {
    // Don't create interval if there are
    // no images or only one image.
    if (sliderImages.length <= 1) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % sliderImages.length);
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [sliderImages.length]);

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return (
      <div className="slider">
        <div className="slider-loading">Loading...</div>
      </div>
    );
  }

  // ============================================================
  // NO IMAGES
  // ============================================================

  if (sliderImages.length === 0) {
    return null;
  }

  // ============================================================
  // CAROUSEL
  // ============================================================

  return (
    <div className="slider">
      {sliderImages.map((image, index) => (
        <div
          key={image.id}
          className={`slider-slide ${index === currentIndex ? "active" : ""}`}
        >
          {/* ==================================================
                CLOUDINARY IMAGE
                ================================================== */}

          <img
            src={image.image}
            alt={image.title || "Carousel Image"}
            loading={index === 0 ? "eager" : "lazy"}
            onError={(event) => {
              console.error("Failed to load carousel image:", image.image);

              event.currentTarget.style.display = "none";
            }}
          />

          {/* ==================================================
                TITLE
                ================================================== */}

          {image.title && <div className="slider-caption">{image.title}</div>}
        </div>
      ))}
    </div>
  );
};

export default Carousel;
